import { useCallback, useMemo } from "react";
import { DateFieldRangeChange, DateFieldRangeProps } from "../DateFieldRange";
import { convertStringToDate } from "../../DateFieldSingle/internal/utils";
import { DateTime } from "luxon";

/**
 * This hook is used to convert the string props of a DateFieldRange component to a DateTime format.
 *
 * @param props - The props of the DateFieldRange component.
 * @returns The normalized value, defaultValue, minDate, maxDate, and unavailable dates in DateTime format.
 * @internal This hook is not intended to be used directly in consumer code.
 */
export const useDateFieldRangeConversion = (props: DateFieldRangeProps) => {
  const { value, defaultValue, minDate, maxDate, unavailable, onChange } =
    props;

  const normalizedValue = useMemo(() => {
    if (value === null || value === undefined) return value;
    return {
      startDate: convertStringToDate(value.startDate) ?? null,
      endDate: convertStringToDate(value.endDate) ?? null,
    };
  }, [value]);

  const normalizedDefaultValue = useMemo(() => {
    if (defaultValue === null || defaultValue === undefined)
      return defaultValue;
    return {
      startDate: convertStringToDate(defaultValue.startDate) ?? null,
      endDate: convertStringToDate(defaultValue.endDate) ?? null,
    };
  }, [defaultValue]);

  const normalizedMinDate = useMemo(
    () => convertStringToDate(minDate),
    [minDate],
  );
  const normalizedMaxDate = useMemo(
    () => convertStringToDate(maxDate),
    [maxDate],
  );

  const normalizedUnavailableDates = useMemo(() => {
    return unavailable?.dates
      ?.map((date) => convertStringToDate(date))
      .filter((date) => date !== null && date !== undefined);
  }, [unavailable?.dates]);

  const handleChange = useCallback(
    (
      change: Omit<DateFieldRangeChange, "startDate" | "endDate"> & {
        startDate: DateTime | null;
        endDate: DateTime | null;
      },
    ) => {
      onChange?.({
        ...change,
        startDate: change.startDate?.toISODate() ?? null,
        endDate: change.endDate?.toISODate() ?? null,
      });
    },
    [onChange],
  );

  return {
    value: normalizedValue,
    defaultValue: normalizedDefaultValue,
    minDate: normalizedMinDate,
    maxDate: normalizedMaxDate,
    unavailable: {
      dates: normalizedUnavailableDates,
      daysOfWeek: unavailable?.daysOfWeek,
    },
    onChange: handleChange,
  };
};
